import { api } from '@/config/api';

export type Provider = 'meta' | 'tiktok' | 'google' | 'whatsapp' | 'x';

export interface ProviderInfo {
  provider: Provider;
  /** Where the account is connected — never gated itself. */
  connectPath: string;
  listUrl: string;
}

const PROVIDERS: Record<Provider, ProviderInfo> = {
  meta: { provider: 'meta', connectPath: '/facebook/accounts', listUrl: '/slighter_crm/facebook/ad-account/list' },
  tiktok: { provider: 'tiktok', connectPath: '/tiktok/account', listUrl: '/slighter_crm/tiktok/accounts' },
  google: { provider: 'google', connectPath: '/google-ads/account', listUrl: '/slighter_crm/google-ads/accounts' },
  whatsapp: { provider: 'whatsapp', connectPath: '/whatsapp-accounts', listUrl: '/slighter_crm/whatsapp-account/list' },
  x: { provider: 'x', connectPath: '/x-ads/account', listUrl: '/slighter_crm/x-ads/accounts' },
};

/** Route prefix → provider, most specific first. Connect pages and OAuth callbacks are left out. */
const ROUTES: Array<[string, Provider | null]> = [
  ['/facebook/accounts', null],
  ['/facebook/ad-account', null],
  ['/facebook', 'meta'],
  ['/instagram', 'meta'],
  ['/tiktok/account', null],
  ['/tiktok', 'tiktok'],
  ['/google-ads/account', null],
  ['/google-ads/youtube/callback', null],
  ['/google-ads', 'google'],
  ['/x-ads/account', null],
  ['/x-ads', 'x'],
  ['/whatsapp-accounts', null],
  ['/whatsapp-account/bulk', 'whatsapp'],
  ['/whatsapp-account/tools', 'whatsapp'],
  ['/whatsapp', 'whatsapp'],
  ['/chats', 'whatsapp'],
  ['/templates', 'whatsapp'],
  ['/campaigns', 'whatsapp'],
];

export function providerForPath(pathname: string): ProviderInfo | null {
  for (const [prefix, provider] of ROUTES) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) return provider ? PROVIDERS[provider] : null;
  }
  return null;
}

// Only "connected" is cached: after someone connects, the next visit must not show the connect page again.
const connectedAt = new Map<Provider, number>();
const CACHE_MS = 60_000;

/** Synchronous read of a recent "connected" answer, so moving between pages doesn't flash a loader. */
export const knownConnected = (provider: Provider) => {
  const hit = connectedAt.get(provider);
  return !!hit && Date.now() - hit < CACHE_MS;
};

/** true = at least one active account; null = could not tell (the page then loads as usual). */
export async function isProviderConnected(provider: Provider): Promise<boolean | null> {
  if (knownConnected(provider)) return true;
  try {
    const rows = (await api.get(PROVIDERS[provider].listUrl, { expectedStatuses: [400, 404, 500, 502, 503] })).data;
    const list: Array<{ isActive?: boolean }> = Array.isArray(rows) ? rows : Array.isArray(rows?.items) ? rows.items : [];
    const connected = list.some((a) => a.isActive !== false);
    if (connected) connectedAt.set(provider, Date.now());
    else connectedAt.delete(provider);
    return connected;
  } catch {
    return null;
  }
}

export function forgetProviderConnection(provider?: Provider) {
  if (provider) connectedAt.delete(provider);
  else connectedAt.clear();
}
